import { Tabs, toast } from "@gotts.ai/ui";
import { useState } from "react";

import { useDeployment, useWallet } from "../../context";
import { useTransactionFlow } from "../../hooks/useTransactionFlow";
import { V3_NFT_POSITION_MANAGER_ABI } from "../../lib/abis";

import { RemoveLiquidityModal } from "./RemoveLiquidityModal";
import { MAX_UINT128 } from "./types";
import { V2PositionsTable } from "./V2PositionsTable";
import { V3PositionsSection } from "./V3PositionsSection";
import { V4PositionsTable } from "./V4PositionsTable";

import type { StepDefinition } from "../../hooks/useTransactionFlow";
import type { V2Position, V3Position, V4Position } from "./types";
// Address import removed (unused)

interface TickBucket {
  tick: number;
  liquidity: number;
}

interface PositionsOverviewProps {
  isConnected: boolean;
  onRefresh: () => void;
  v3Positions: V3Position[];
  v3Loading: boolean;
  poolTicks: Record<string, number>;
  liqBuckets: TickBucket[];
  liqLoading: boolean;
  refetchV3: () => void;
  refetchBalances: () => void;
  v4Positions: V4Position[];
  v4Loading: boolean;
  v4PoolTicks: Record<string, number>;
  refetchV4: () => void;
  v2Positions: V2Position[];
  v2Loading: boolean;
  refetchV2: () => void;
}

type PositionTab = "V2" | "V3" | "V4";

export function PositionsOverview({
  isConnected,
  onRefresh,
  v3Positions,
  v3Loading,
  poolTicks,
  liqBuckets,
  liqLoading,
  refetchV3,
  refetchBalances,
  v4Positions,
  v4Loading,
  v4PoolTicks,
  refetchV4,
  v2Positions,
  v2Loading,
  refetchV2,
}: PositionsOverviewProps) {
  const { deployment } = useDeployment();
  const { walletClient, address, chain } = useWallet();
  const flow = useTransactionFlow();
  const [activeTab, setActiveTab] = useState<PositionTab>("V2");
  const [removeModal, setRemoveModal] = useState<{
    type: "V2" | "V3" | "V4";
    v2Position?: V2Position;
    v3Position?: V3Position;
    v4Position?: V4Position;
    currentTick?: number;
  } | null>(null);

  async function handleCollectFees(tokenId: bigint) {
    if (!walletClient || !address || !deployment?.v3) return;

    const npm = deployment.v3.nonfungiblePositionManager;
    const steps: StepDefinition[] = [
      {
        label: "Collect Fees",
        execute: async () =>
          walletClient.writeContract({
            chain,
            account: address,
            address: npm,
            abi: V3_NFT_POSITION_MANAGER_ABI,
            functionName: "collect",
            args: [
              {
                tokenId,
                recipient: address,
                amount0Max: MAX_UINT128,
                amount1Max: MAX_UINT128,
              },
            ],
          }),
      },
    ];

    const toastId = toast.loading(
      `Collecting fees for position #${tokenId.toString()}...`,
    );
    await flow.execute(steps, () => {
      toast.dismiss(toastId);
      toast.success(`Collected fees for position #${tokenId.toString()}`);
      refetchBalances();
      refetchV3();
      onRefresh();
    });
    toast.dismiss(toastId);
  }

  const tabs = [
    { label: `V2 (${String(v2Positions.length)})`, value: "V2" as const },
    { label: `V3 (${String(v3Positions.length)})`, value: "V3" as const },
    { label: `V4 (${String(v4Positions.length)})`, value: "V4" as const },
  ];

  return (
    <>
      <div>
        <div className="mb-5 flex items-end justify-between">
          <h2 className="type-section-title">Positions</h2>
          <span className="font-mono text-[11px] text-[var(--color-text-muted)]">
            {v2Positions.length + v3Positions.length + v4Positions.length} total
          </span>
        </div>
        <Tabs
          tabs={tabs}
          value={activeTab}
          onChange={(v) => {
            setActiveTab(v as PositionTab);
          }}
        />

        <div className="mt-6 pt-1">
          {activeTab === "V2" && (
            <V2PositionsTable
              positions={v2Positions}
              loading={v2Loading}
              onRemove={(pos) => {
                setRemoveModal({ type: "V2", v2Position: pos });
              }}
            />
          )}
          {activeTab === "V3" && (
            <V3PositionsSection
              positions={v3Positions}
              loading={v3Loading}
              poolTicks={poolTicks}
              liqBuckets={liqBuckets}
              liqLoading={liqLoading}
              isConnected={isConnected}
              onCollectFees={handleCollectFees}
              onRemove={(pos) => {
                const key = `${pos.token0.toLowerCase()}:${pos.token1.toLowerCase()}:${String(pos.fee)}`;
                setRemoveModal({
                  type: "V3",
                  v3Position: pos,
                  currentTick: poolTicks[key],
                });
              }}
              isExecuting={flow.isExecuting}
            />
          )}
          {activeTab === "V4" && (
            <V4PositionsTable
              positions={v4Positions}
              poolTicks={v4PoolTicks}
              loading={v4Loading}
              onRemove={(pos) => {
                const key = `${pos.currency0.toLowerCase()}:${pos.currency1.toLowerCase()}:${String(pos.fee)}`;
                setRemoveModal({
                  type: "V4",
                  v4Position: pos,
                  currentTick: v4PoolTicks[key],
                });
              }}
              isExecuting={flow.isExecuting}
            />
          )}
        </div>
      </div>

      {/* Remove Liquidity Modal */}
      {removeModal && (
        <RemoveLiquidityModal
          type={removeModal.type}
          v2Position={removeModal.v2Position}
          v3Position={removeModal.v3Position}
          v4Position={removeModal.v4Position}
          currentTick={removeModal.currentTick}
          onClose={() => {
            setRemoveModal(null);
          }}
          onSuccess={() => {
            refetchBalances();
            refetchV2();
            refetchV3();
            refetchV4();
            onRefresh();
          }}
        />
      )}
    </>
  );
}
